import { Helmet } from "react-helmet-async";
import { useState, useMemo } from "react";
// @mui
import {
  Card,
  Stack,
  Container,
  Typography,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import {
  Download,
  Business,
  PersonAdd,
  Info,
} from "@mui/icons-material";
import { GridActionsCellItem } from "@mui/x-data-grid";

// components
import Scrollbar from "../components/scrollbar";
import Protected from "../protected/Protected";
import Iconify from "../components/iconify";
// sections
import CustomTable from "../components/data-table/CustomDataTable";
import {
  useGetAllUserSubscriptionsQuery,
  useLazyGetAllUserSubscriptionsQuery,
  useGetAllUserDocumentTopUpsQuery,
  useLazyGetAllUserDocumentTopUpsQuery,
} from "../redux/services/subscription.service";
import useDownloadAll from "../hooks/useDownloadAll";
import OrganizationSubscriptionForm from "../sections/@dashboard/subscription/organizationSubscription.form";
import ManualSubscriptionForm from "../sections/@dashboard/subscription/manualSubscription.form";

// ----------------------------------------------------------------------

const SUBSCRIPTION_TYPES = [
  { value: "all", label: "All" },
  { value: "razorpay", label: "Razorpay" },
  { value: "razorpay_orders", label: "Razorpay Orders" },
  { value: "organization", label: "Organization" },
  { value: "device", label: "Device" },
  { value: "manual", label: "Manual" },
];

const DOCUMENT_TOPUP_STATUS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "authenticated", label: "Authenticated" },
  { value: "pending", label: "Pending" },
  { value: "expired", label: "Expired" },
];

export default function SubscriptionPage() {
  // Tab state for switching between Subscriptions and Document Top-ups
  const [tabValue, setTabValue] = useState(0);
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState("");
  const [selectedTopUpStatus, setSelectedTopUpStatus] = useState("");

  const subscriptionQueryParams = useMemo(
    () => ({
      ...(selectedSubscriptionType &&
        selectedSubscriptionType !== "all" && {
          subscriptionType: selectedSubscriptionType,
        }),
    }),
    [selectedSubscriptionType],
  );

  const topUpQueryParams = useMemo(
    () => ({
      ...(selectedTopUpStatus &&
        selectedTopUpStatus !== "all" && {
          status: selectedTopUpStatus,
        }),
    }),
    [selectedTopUpStatus],
  );

  const handleResetSubscriptionFilter = () => {
    setSelectedSubscriptionType("");
  };

  const handleResetTopUpFilter = () => {
    setSelectedTopUpStatus("");
  };

  // Subscription form states
  const [openOrgSubscriptionForm, setOpenOrgSubscriptionForm] = useState(false);
  const [openManualSubscriptionForm, setOpenManualSubscriptionForm] =
    useState(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Download hooks for both tabs
  const {
    handleDownloadAll: handleDownloadAllSubscriptions,
    isDownloading: isDownloadingSubscriptions,
  } = useDownloadAll(useLazyGetAllUserSubscriptionsQuery, "Subscriptions");

  const {
    handleDownloadAll: handleDownloadAllTopUps,
    isDownloading: isDownloadingTopUps,
  } = useDownloadAll(useLazyGetAllUserDocumentTopUpsQuery, "DocumentTopUps");

  const handleDownloadAll = () => {
    if (tabValue === 0) {
      handleDownloadAllSubscriptions({
        ...(selectedSubscriptionType && selectedSubscriptionType !== "all" && {
          subscriptionType: selectedSubscriptionType,
        }),
      });
    } else {
      handleDownloadAllTopUps({
        ...(selectedTopUpStatus && selectedTopUpStatus !== "all" && {
          status: selectedTopUpStatus,
        }),
      });
    }
  };

  const isDownloading = isDownloadingSubscriptions || isDownloadingTopUps;

  const handleDownload = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  return (
    <Protected>
      <Helmet>
        <title> Subscription </title>
      </Helmet>

      <Container>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          mb={5}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
              {tabValue === 0 ? "Subscriptions" : "Document Top-ups"}
            </Typography>
            {tabValue === 0 && (
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Subscription Status Guide:
                    </Typography>
                    <Typography variant="body2">
                      <strong>Active:</strong> Ongoing subscription
                    </Typography>
                    <Typography variant="body2">
                      <strong>Authenticated:</strong> Upgraded subscription
                    </Typography>
                    <Typography variant="body2">
                      <strong>Completed:</strong> Subscription billing cycle
                      completed
                    </Typography>
                  </Box>
                }
                arrow
                placement="right"
              >
                <IconButton size="small" sx={{ mt: -1 }}>
                  <Info fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {tabValue === 0 && (
              <>
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => setOpenManualSubscriptionForm(true)}
                >
                  Manual Subscription
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Business />}
                  onClick={() => setOpenOrgSubscriptionForm(true)}
                >
                  Organization Subscription
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              startIcon={
                isDownloading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Iconify icon="eva:download-fill" />
                )
              }
              onClick={handleDownloadAll}
              disabled={isDownloading}
            >
              Download All
            </Button>
          </Stack>
        </Stack>

        {/* Tabs for Subscriptions / Document Top-ups */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Subscriptions" />
            <Tab label="Document Top-ups" />
          </Tabs>
        </Box>

        {/* Subscriptions Tab */}
        {tabValue === 0 && (
          <Card>
            <Scrollbar>
              <CustomTable
                canSelect
                useDataQuery={useGetAllUserSubscriptionsQuery}
                tableName="Subscription"
                searchLabel="Search Subscription..."
                searchCols={["invoiceNumber", "planName", "code", "status"]}
                defaultQueryParams={subscriptionQueryParams}
                resetParentForm={handleResetSubscriptionFilter}
                extraHeaderField={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      ml: 2,
                    }}
                  >
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel id="subscription-type-label">
                        Subscription Type
                      </InputLabel>
                      <Select
                        labelId="subscription-type-label"
                        id="subscription-type-select"
                        value={selectedSubscriptionType}
                        label="Subscription Type"
                        onChange={(e) =>
                          setSelectedSubscriptionType(e.target.value)
                        }
                      >
                        {SUBSCRIPTION_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                }
                actions={{
                  field: "actions",
                  headerName: "Download",
                  type: "actions",
                  width: 100,
                  getActions: ({ row }) =>
                    [
                      row?.invoicePdfUrl ? (
                        <Tooltip title="Download Invoice PDF" key="download">
                          <GridActionsCellItem
                            icon={<Download color="primary" />}
                            label="Download Invoice"
                            onClick={() => handleDownload(row.invoicePdfUrl)}
                          />
                        </Tooltip>
                      ) : null,
                    ].filter(Boolean),
                }}
              />
            </Scrollbar>
          </Card>
        )}

        {/* Document Top-ups Tab */}
        {tabValue === 1 && (
          <Card>
            <Scrollbar>
              <CustomTable
                canSelect
                useDataQuery={useGetAllUserDocumentTopUpsQuery}
                tableName="DocumentTopUp"
                searchLabel="Search Document Top-up..."
                searchCols={[
                  "firstName",
                  "lastName",
                  "email",
                  "contactNumber",
                  "planName",
                  "status",
                ]}
                defaultQueryParams={topUpQueryParams}
                resetParentForm={handleResetTopUpFilter}
                extraHeaderField={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      ml: 2,
                    }}
                  >
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel id="topup-status-label">Status</InputLabel>
                      <Select
                        labelId="topup-status-label"
                        id="topup-status-select"
                        value={selectedTopUpStatus}
                        label="Status"
                        onChange={(e) => setSelectedTopUpStatus(e.target.value)}
                      >
                        {DOCUMENT_TOPUP_STATUS.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                }
                actions={{
                  field: "actions",
                  headerName: "Download",
                  type: "actions",
                  width: 100,
                  getActions: ({ row }) =>
                    [
                      row?.invoicePdfUrl ? (
                        <Tooltip title="Download Invoice PDF" key="download">
                          <GridActionsCellItem
                            icon={<Download color="primary" />}
                            label="Download Invoice"
                            onClick={() => handleDownload(row.invoicePdfUrl)}
                          />
                        </Tooltip>
                      ) : null,
                    ].filter(Boolean),
                }}
              />
            </Scrollbar>
          </Card>
        )}
      </Container>

      <OrganizationSubscriptionForm
        open={openOrgSubscriptionForm}
        onClose={() => setOpenOrgSubscriptionForm(false)}
      />

      <ManualSubscriptionForm
        open={openManualSubscriptionForm}
        onClose={() => setOpenManualSubscriptionForm(false)}
      />
    </Protected>
  );
}
